/**
 * Condition chip HTML 생성 — shared 의 conditionDisplay 활용.
 *
 * 사용:
 *   import { renderConditionChip } from '../lib/condition-chip';
 *   ${renderConditionChip(item.requested_condition)}
 *
 * 출력 (chip 2 줄):
 *   <span class="cond-chip cond-신품">신품</span> <code>F/S</code>
 *   <span class="cond-chip cond-미확인">미확인</span>   ← 세부 없음
 *
 * 그룹별 색상 (CSS):
 *   .cond-신품  → 초록 (--primary)
 *   .cond-중고  → 노랑
 *   .cond-미확인 → 회색
 */

import {
  conditionDisplay,
  conditionDisplayList,
  isPartUnregistered,
  type ConditionGroup,
} from './condition';
import { escapeHtml } from './cells';

/**
 * 단일값 → chip 1개 (group + 세부).
 * 멀티값 ('신품, 중고') 도 처리하고 싶으면 renderConditionChips 사용.
 */
export function renderConditionChip(raw: string | null | undefined): string {
  const { group, detail } = conditionDisplay(raw);
  const groupHtml = `<span class="cond-chip cond-${escapeHtml(group)}">${escapeHtml(group)}</span>`;
  if (!detail) return groupHtml;
  return `${groupHtml} <code class="cond-detail">${escapeHtml(detail)}</code>`;
}

/**
 * 멀티값 → chip 여러 개 (노션 multi-select 콤마 구분 처리).
 *
 * 예:
 *   renderConditionChips('신품, 중고')  → [신품] [중고]
 *   renderConditionChips('F/S, REF')   → [신품] F/S  [중고] REF
 *   renderConditionChips(null)         → [미확인]
 *
 * 영업담당이 노션 multi-select 로 입력한 멀티값을 그대로 표시.
 */
export function renderConditionChips(raw: string | null | undefined): string {
  const list = conditionDisplayList(raw);
  return list.map(({ group, detail }) => {
    const g = `<span class="cond-chip cond-${escapeHtml(group)}">${escapeHtml(group)}</span>`;
    return detail
      ? `${g} <code class="cond-detail">${escapeHtml(detail)}</code>`
      : g;
  }).join(' ');
}

/** group 만 (chip 한 줄). NeedRfqs / 소싱 등에서 좁은 컬럼용. */
export function renderConditionGroupOnly(raw: string | null | undefined): string {
  const { group } = conditionDisplay(raw);
  return `<span class="cond-chip cond-${escapeHtml(group)}">${escapeHtml(group)}</span>`;
}

/**
 * 품목 등록 상태 chip — option_part 가 NULL/빈 이면 `[미등록]`.
 * 등록된 품목 (option_part 있음) 이면 빈 문자열 (chip 표시 안 함).
 *
 * 사용:
 *   ${renderRegistrationChip(it.option_part)} ${renderConditionChips(it.requested_condition)}
 */
export function renderRegistrationChip(optionPart: string | null | undefined): string {
  if (!isPartUnregistered(optionPart)) return '';
  return `<span class="cond-chip cond-미등록">미등록</span>`;
}

export type { ConditionGroup };
